import React from 'react';
import { cn } from '../../utils/cn';
import { generateId } from '../../utils/accessibility';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  onRightIconClick?: () => void;
  ref?: React.Ref<HTMLInputElement>;
}

const Input: React.FC<InputProps> = ({
  className,
  type,
  label,
  error,
  helperText,
  leftIcon,
  rightIcon,
  onRightIconClick,
  id,
  'aria-describedby': ariaDescribedBy,
  ref,
  ...props
}) => {
  const inputId = id || generateId('input');
  const errorId = error ? `${inputId}-error` : undefined;
  const helperTextId = helperText ? `${inputId}-helper` : undefined;

  const describedBy =
    [ariaDescribedBy, errorId, helperTextId].filter(Boolean).join(' ') ||
    undefined;

  return (
    <div className="space-y-1">
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-gray-300"
        >
          {label}
          {props.required && (
            <span className="text-error-400 ml-1" aria-label="required">
              *
            </span>
          )}
        </label>
      )}

      <div className="relative">
        {leftIcon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <div className="text-gray-400" aria-hidden="true">
              {leftIcon}
            </div>
          </div>
        )}

        <input
          id={inputId}
          type={type}
          className={cn(
            'flex h-10 w-full rounded-md border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50 transition-colors',
            error && 'border-error-500 focus:ring-error-500',
            leftIcon && 'pl-10',
            rightIcon && 'pr-10',
            className
          )}
          ref={ref}
          aria-describedby={describedBy}
          aria-invalid={error ? 'true' : 'false'}
          {...props}
        />

        {rightIcon && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            {onRightIconClick ? (
              <button
                type="button"
                onClick={onRightIconClick}
                className="text-gray-400 hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
                aria-label="Toggle input action"
              >
                {rightIcon}
              </button>
            ) : (
              <div className="text-gray-400" aria-hidden="true">
                {rightIcon}
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <p
          id={errorId}
          className="text-sm text-error-400"
          role="alert"
          aria-live="polite"
        >
          {error}
        </p>
      )}

      {helperText && !error && (
        <p id={helperTextId} className="text-sm text-gray-400">
          {helperText}
        </p>
      )}
    </div>
  );
};

export { Input };
